import React, { useCallback, ChangeEvent, KeyboardEvent } from 'react';
import cn from 'classnames';
import { IconButton, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { KEY_CODES } from 'utils';

import { useStyles } from './mui-input.styles';

interface MuiSearchFieldProps {
	name: string;
	label: string;
	value: string | undefined;
	handleChange: (q: string, fieldName: string) => void;
	wrapperClass?: string;
}

export const MuiSearchField = ({
	name,
	label,
	value,
	handleChange,
	wrapperClass,
}: MuiSearchFieldProps) => {
	const classes = useStyles();

	const onChange = useCallback(
		({
			target: { value: v },
		}: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
			handleChange(v, name),
		[handleChange],
	);

	const clearSearchField = useCallback(
		(targetEl: HTMLInputElement) => {
			handleChange('', name);
			targetEl.blur();
		},
		[handleChange],
	);

	const handleKeyUp = useCallback(
		(e: KeyboardEvent<HTMLDivElement>) => {
			if (e.key === KEY_CODES.ESCAPE) {
				clearSearchField(e.target as HTMLInputElement);
			}
		},
		[clearSearchField],
	);

	const onCloseButtonClick = useCallback(
		({ target }: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
			clearSearchField(target as HTMLInputElement),
		[clearSearchField],
	);

	return (
		<TextField
			className={cn(wrapperClass, classes.textField)}
			label={label}
			id={name}
			value={value}
			onChange={onChange}
			onKeyUp={handleKeyUp}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end">
						<IconButton edge="end" onClick={onCloseButtonClick} size="large">
							<CloseIcon />
						</IconButton>
						<IconButton className={classes.searchButton} size="large">
							<SearchIcon />
						</IconButton>
					</InputAdornment>
				),
			}}
			variant="outlined"
		/>
	);
};
